import React, { useState, useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { CircularProgress } from "@mui/material";
import { useCartOperationStatus } from "../../../../hooks/useCartOperationStatus";
import { useCartLocationStatus } from "../../../../hooks/useCartLocationStatus";
import { calculateItemsSubtotal } from "../../../../utils/cartCalculationUtils";
import { CleanCartItem } from "../../../../types/cleanPricing";
import { getDeliveryMethodLabel } from "../../../../utils/deliveryMethodUtils";
import CartItem from "../CartItem/CartItem";
import DeliveryDatePicker from "../DeliveryDatePicker";
import GroupMessages, { MessageType } from "../GroupMessages/GroupMessages";
import useDeliveryDateUpdate from "../../../../hooks/useDeliveryDateUpdate";
import { UnifiedDeliveryCalculationService } from "../../../../services/UnifiedDeliveryCalculationService";
import { CartDeliveryService } from "../../../../services/CartDeliveryService";
import { DateService } from "../../../../utils/dateUtils";
import { useDebounceCartUpdates } from "../../../../hooks/useDebounceCartUpdates";
import { useCartMessages } from "../../../../hooks/useCartMessages";
import styles from "./CartGroup.module.css";

interface CartProductType {
  cartCustomId: string;
  quantity: number;
  price: number;
  finalPrice: number;
  cleanPricing?: CleanCartItem; // Phase 2.3.3: Clean pricing data from backend
  product: {
    id?: string;
    documentId?: string;
    sku?: string;
    stock: number;
    reservedStock: number;
    logistics?: {
      weight: number;
      expressDelivery?: boolean;
      limitTime?: string;
      minDaysToDeliver?: number;
      daysToDeliver?: number;
    };
    partner?: {
      daysOpen?: {
        monday: boolean;
        tuesday: boolean;
        wednesday: boolean;
        thursday: boolean;
        friday: boolean;
        saturday: boolean;
        sunday: boolean;
        mondayOpeningTime: string;
        mondayClosingTime: string;
        tuesdayOpeningTime: string;
        tuesdayClosingTime: string;
        wednesdayOpeningTime: string;
        wednesdayClosingTime: string;
        thursdayOpeningTime: string;
        thursdayClosingTime: string;
        fridayOpeningTime: string;
        fridayClosingTime: string;
        saturdayOpeningTime: string;
        saturdayClosingTime: string;
        sundayOpeningTime: string;
        sundayClosingTime: string;
      };
      // Direct zone access (current structure)
      deliveryLeadTimeZone?: { documentId: string };
      deliveryFeeZone?: { documentId: string };
      // Legacy nested structure (for backward compatibility)
      originZones?: {
        deliveryLeadTimeZone?: { documentId: string };
        deliveryFeeZone?: { documentId: string };
      };
    };
  };
}

interface CartGroupType {
  id: string;
  deliveryDate?: string;
  deliveryMethod?: string; // Phase 2.2: Add delivery method for method selection
  items: CartProductType[]; // Phase 3.2: Updated to match clean structure format
  subTotal?: number;
  deliveryFee?: number; // Clean structure field for delivery fee
  totalDeliveryFee?: number; // Legacy field for backward compatibility
  // FIX: groupTotals should be a flat object, not a keyed map
  groupTotals?: {
    originalSubtotal: number;
    customizationTotal: number;
    promotionalDiscountTotal: number;
    couponDiscountTotal: number;
    finalSubtotal: number;
    deliveryFeeTotal: number;
    groupTotal: number;
  };
  // Add legacy fields for fallback
  promotionalDiscountAmount?: number;
  couponDiscountAmount?: number;
  // Add delivery zone information that may be needed for calculations
  deliveryFeeZone?: {
    documentId: string;
    [key: string]: any;
  };
  deliveryLeadTimeZone?: {
    documentId: string;
    [key: string]: any;
  };
}

interface StockValidationItemType {
  cartCustomId: string;
  productId: string;
  productName: string;
  requestedQuantity: number;
  availableStock: number;
  isValid: boolean;
}

interface StockValidationGroupType {
  groupId: string;
  isValid: boolean;
  items: StockValidationItemType[];
}

interface Props {
  group: CartGroupType;
  index: number;
  onDeliveryDateChange: (groupId: string, deliveryDate: string) => void;
  refreshCart: () => void;
  handleCartAmountChange: (quantity: number, item: any) => void;
  handleRemoveItem: (item: any) => void;
  isStandbyGroup?: boolean;
  stockValidation?: StockValidationGroupType;
  // Coupon-related props
  appliedCouponCode?: string | null;
  couponDetails?: any;
  affectedProductIds?: string[];
}

const CartGroup: React.FC<Props> = ({
  group,
  index,
  onDeliveryDateChange,
  refreshCart,
  handleCartAmountChange,
  handleRemoveItem,
  isStandbyGroup = false,
  stockValidation,
  appliedCouponCode,
  couponDetails,
  affectedProductIds = [],
}) => {
  const [minDate, setMinDate] = useState<Date>(new Date());
  const [latestStandardDeliveryDate, setLatestStandardDeliveryDate] =
    useState<Date>(new Date());
  const [stockError, setStockError] = useState<boolean>(false);
  const [outOfStockItems, setOutOfStockItems] = useState<string[]>([]);
  const [isEditing, setIsEditing] = useState<boolean>(!group.deliveryDate);
  const [isFeeCalculating, setIsFeeCalculating] = useState<boolean>(false);
  const [needsDeliveryDate, setNeedsDeliveryDate] = useState<boolean>(false);
  const [canShowDatePicker, setCanShowDatePicker] = useState(false);
  const [isDatePickerExiting, setIsDatePickerExiting] = useState(false);
  const [isCalculatingDates, setIsCalculatingDates] = useState(false);

  // State for available dates from delivery method calculation
  const [availableDeliveryDates, setAvailableDeliveryDates] = useState<Date[]>(
    []
  );
  const [selectedDeliveryMethod, setSelectedDeliveryMethod] =
    useState<string>("");

  const prevDeliveryDateRef = useRef<string | undefined>(group.deliveryDate);
  const prevDestinationZoneRef = useRef<string | null>(null);

  const prevOperationTypeRef = useRef<string | null>(null);
  const lastCompletedOperationRef = useRef<string | null>(null);

  const hasCalculatedMinDate = useRef<boolean>(false);

  const calculatedMinDateRef = useRef<Date | null>(null);

  const {
    messages,
    isExiting: isMessagesExiting,
    showLoadingMessage,
    showSuccessMessage,
    showErrorMessage,
    showWarningMessage,
    hideMessage,
    hideAllMessages,
    handleMessagesExited,
  } = useCartMessages();

  const destinationLeadTimeZoneId = useSelector(
    (state: any) => state.location.deliveryLeadTimeZone?.documentId
  );
  const destinationFeeZoneId = useSelector(
    (state: any) => state.location.deliveryFeeZone?.documentId
  );
  const hasLocationSet = useSelector(
    (state: any) =>
      !!state.location?.address && !!state.location.deliveryFeeZone?.documentId
  );

  const { locationUpdateJustCompleted } = useCartLocationStatus();
  const { isLoading: isGroupUpdating, operationType } = useCartOperationStatus(
    group.id
  );
  const { updateDeliveryDate } = useDeliveryDateUpdate();

  // Phase 2.2: Group-level delivery method change functionality removed
  // Now using per-product delivery method selection in CartItem

  const {
    handleIncrease,
    handleDecrease,
    handleRemove,
    getOptimisticQuantity,
  } = useDebounceCartUpdates(1200);

  // Phase 2.3.3: Access pricing loading state from Redux
  const isPricingLoading = useSelector(
    (state: any) => state.cart.pricingLoading?.groups?.[group.id] || false
  );

  // Add state for tracking calculation timing
  const [calculationStartTime, setCalculationStartTime] = useState<
    number | null
  >(null);

  useEffect(() => {
    const fetchLatestStandardDeliveryDate = async () => {
      if (!destinationLeadTimeZoneId || !destinationFeeZoneId) {
        return;
      }

      console.log(
        `🔄 [CART-GROUP] Starting delivery date calculation for group ${group.id}:`,
        {
          groupId: group.id,
          deliveryMethod: group.deliveryMethod || "standard",
          destinationLeadTimeZoneId,
          destinationFeeZoneId,
          itemsCount: group.items.length,
        }
      );

      setIsCalculatingDates(true);
      setCalculationStartTime(Date.now()); // Start timing
      try {
        // Validate we have required data before proceeding
        const firstItem = group.items[0];
        if (
          !firstItem?.product?.logistics ||
          !firstItem?.product?.partner?.daysOpen
        ) {
          console.warn(
            "CartGroup: Missing required logistics or partner data for delivery calculation"
          );
          throw new Error("Insufficient product data for delivery calculation");
        }

        // Validate required zone data
        const originFeeZoneId =
          firstItem?.product?.partner?.deliveryFeeZone?.documentId;
        const originLeadTimeZoneId =
          firstItem?.product?.partner?.deliveryLeadTimeZone?.documentId;

        if (!originFeeZoneId || !originLeadTimeZoneId) {
          console.warn(
            "CartGroup: Missing required zone data for delivery calculation",
            {
              originFeeZoneId,
              originLeadTimeZoneId,
              firstItemId: firstItem.cartCustomId,
            }
          );
          throw new Error("Missing zone data for delivery calculation");
        }

        // Validate limitTime to ensure it's a valid string
        const limitTime = firstItem.product.logistics.limitTime;
        if (!limitTime || typeof limitTime !== "string") {
          console.warn("CartGroup: Invalid or missing limitTime", {
            limitTime,
            firstItemId: firstItem.cartCustomId,
          });
          throw new Error("Invalid limitTime for delivery calculation");
        }

        // Determine which delivery method to use for date calculation
        const deliveryMethod =
          (group.deliveryMethod as "express" | "rapido" | "standard") ||
          "standard";

        console.log(
          `📅 [CART-GROUP] Calculating ${deliveryMethod} delivery date for group ${group.id}:`,
          {
            groupId: group.id,
            method: deliveryMethod,
            itemsCount: group.items.length,
          }
        );

        // Use the new method-specific date calculation with error handling
        let earliestDate;
        try {
          console.log(
            `🚨 [CART-GROUP] About to call CartDeliveryService.getEarliestDateForDeliveryMethod:`,
            {
              groupId: group.id,
              method: deliveryMethod,
              itemsToProcess: group.items.length,
              firstItemDetails: {
                cartCustomId: group.items[0]?.cartCustomId,
                productId:
                  group.items[0]?.product?.id ||
                  group.items[0]?.product?.documentId,
                hasLogistics: !!group.items[0]?.product?.logistics,
                hasPartner: !!group.items[0]?.product?.partner,
              },
              destinationZones: {
                leadTime: destinationLeadTimeZoneId,
                fee: destinationFeeZoneId,
              },
            }
          );

          earliestDate =
            await CartDeliveryService.getEarliestDateForDeliveryMethod(
              group.items,
              deliveryMethod,
              destinationLeadTimeZoneId,
              destinationFeeZoneId
            );
        } catch (serviceError) {
          console.error("CartDeliveryService error:", serviceError);

          // Check if this is a "no delivery methods available" error
          if (
            serviceError instanceof Error &&
            serviceError.message.includes("No") &&
            serviceError.message.includes("delivery dates available")
          ) {
            // Show business-rule compliant error message
            const noDeliveryErrorId = `no-delivery-${group.id}`;
            showErrorMessage(
              "No delivery dates available for your location. Please try again later.",
              undefined,
              8000,
              noDeliveryErrorId
            );

            console.error(
              `❌ [CART-GROUP] No delivery methods available for group ${group.id}:`,
              {
                groupId: group.id,
                method: deliveryMethod,
                error: serviceError.message,
                businessRule: "Show error to user, log for manual review",
              }
            );

            // Don't set any date - let the component handle the no-date state
            return;
          }

          // For other errors, fall back to tomorrow but log the issue
          console.warn("Using fallback date due to service error");
          earliestDate = new Date();
          earliestDate.setDate(earliestDate.getDate() + 1);
        }

        // Validate the returned date
        if (!earliestDate || isNaN(earliestDate.getTime())) {
          console.warn("Invalid date from service, using fallback");
          earliestDate = new Date();
          earliestDate.setDate(earliestDate.getDate() + 1);
        }

        if (earliestDate && !isNaN(earliestDate.getTime())) {
          calculatedMinDateRef.current = earliestDate;
          hasCalculatedMinDate.current = true;

          console.log(
            `✅ [CART-GROUP] Set minDate from CartDeliveryService for ${deliveryMethod} method:`,
            {
              groupId: group.id,
              method: deliveryMethod,
              calculatedMinDate: {
                iso: earliestDate.toISOString(),
                local: earliestDate.toLocaleDateString("es-PE"),
              },
              source: "CartDeliveryService.getEarliestDateForDeliveryMethod",
              itemCount: group.items.length,
            }
          );

          console.log(
            `✅ [CART-GROUP] Method-specific date calculated for group ${group.id}:`,
            {
              groupId: group.id,
              method: deliveryMethod,
              calculatedDate: {
                iso: earliestDate.toISOString(),
                local: earliestDate.toLocaleDateString("es-PE"),
                dayName: earliestDate.toLocaleDateString("es-PE", {
                  weekday: "long",
                }),
                daysFromNow: Math.ceil(
                  (earliestDate.getTime() - new Date().getTime()) /
                    (1000 * 60 * 60 * 24)
                ),
              },
              isMethodSpecific: deliveryMethod !== "standard",
              source: "CartDeliveryService.getEarliestDateForDeliveryMethod",
              itemCount: group.items.length,
              willSetMinDate: true,
              willSetLatestStandardDate: true,
            }
          );

          setMinDate(earliestDate);
          setLatestStandardDeliveryDate(earliestDate);

          // Validate calculated date against business rules
          const dateValidation = validateDateAgainstBusinessRules(
            earliestDate,
            deliveryMethod
          );
          if (!dateValidation.isValid) {
            console.warn(
              `⚠️ [CART-GROUP] Calculated date violates business rules for group ${group.id}:`,
              {
                groupId: group.id,
                method: deliveryMethod,
                calculatedDate: {
                  iso: earliestDate.toISOString(),
                  local: earliestDate.toLocaleDateString("es-PE"),
                  dayName: earliestDate.toLocaleDateString("es-PE", {
                    weekday: "long",
                  }),
                },
                violation: dateValidation.reason,
                businessRule:
                  "Standard must not overlap with Rapido, no Sunday deliveries",
              }
            );
          }
        } else {
          const todayLima = new Date();
          const noonToday = new Date(
            todayLima.getFullYear(),
            todayLima.getMonth(),
            todayLima.getDate(),
            12,
            0,
            0
          );

          console.log(
            `⚠️ [CART-GROUP] No valid date from service, using today as fallback:`,
            {
              groupId: group.id,
              fallbackMinDate: {
                iso: noonToday.toISOString(),
                local: noonToday.toLocaleDateString("es-PE"),
              },
              originalDate: "N/A - using CartDeliveryService",
              source: "fallback-today",
            }
          );

          setMinDate(noonToday);
          setLatestStandardDeliveryDate(noonToday);

          if (!calculatedMinDateRef.current) {
            calculatedMinDateRef.current = noonToday;
            hasCalculatedMinDate.current = true;
          }
        }
      } catch (error) {
        console.error("Error calculating delivery dates:", error);
        const todayLima = new Date();
        const noonToday = new Date(
          todayLima.getFullYear(),
          todayLima.getMonth(),
          todayLima.getDate(),
          12,
          0,
          0
        );
        setMinDate(noonToday);
        setLatestStandardDeliveryDate(noonToday);

        if (!calculatedMinDateRef.current) {
          calculatedMinDateRef.current = noonToday;
          hasCalculatedMinDate.current = true;
        }
      } finally {
        const endTime = performance.now();
        const duration = endTime - (calculationStartTime || endTime);

        console.log(
          `⏱️ [CART-GROUP] Calculation completed for group ${group.id}:`,
          {
            groupId: group.id,
            endTime: new Date().toISOString(),
            duration: `${duration.toFixed(2)}ms`,
          }
        );

        setIsCalculatingDates(false);
        setCalculationStartTime(null);
      }
    };

    if (
      destinationLeadTimeZoneId &&
      destinationFeeZoneId &&
      (!hasCalculatedMinDate.current ||
        prevDestinationZoneRef.current !== destinationFeeZoneId)
    ) {
      if (
        prevDestinationZoneRef.current !== destinationFeeZoneId &&
        prevDestinationZoneRef.current
      ) {
        calculatedMinDateRef.current = null;
        hasCalculatedMinDate.current = false;
      }

      fetchLatestStandardDeliveryDate();
    } else if (calculatedMinDateRef.current) {
      setMinDate(calculatedMinDateRef.current);
      setLatestStandardDeliveryDate(calculatedMinDateRef.current);
    }

    prevDestinationZoneRef.current = destinationFeeZoneId;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    group,
    destinationLeadTimeZoneId,
    destinationFeeZoneId,
    showErrorMessage,
  ]);

  useEffect(() => {
    const hasStockIssue = group.items.some(
      (product: CartProductType) => product.product.stock < product.quantity
    );
    setStockError(hasStockIssue);
  }, [group.items]);

  useEffect(() => {
    if (stockValidation && !stockValidation.isValid) {
      const invalidItems = stockValidation.items
        .filter((item) => !item.isValid)
        .map((item) => item.cartCustomId);

      setOutOfStockItems(invalidItems);
      setStockError(!stockValidation.isValid);
    } else {
      setOutOfStockItems([]);
    }
  }, [stockValidation]);

  const [optimisticDeliveryDate, setOptimisticDeliveryDate] = useState<
    string | undefined
  >(group.deliveryDate);

  useEffect(() => {
    if (
      group.deliveryDate !== optimisticDeliveryDate &&
      !optimisticDeliveryDate
    ) {
      setOptimisticDeliveryDate(group.deliveryDate);
    }

    if (
      !optimisticDeliveryDate &&
      !isStandbyGroup &&
      hasLocationSet &&
      !isEditing
    ) {
      setIsEditing(true);
      setIsDatePickerExiting(false);
    }

    setNeedsDeliveryDate(
      !isStandbyGroup &&
        destinationLeadTimeZoneId &&
        !optimisticDeliveryDate &&
        hasLocationSet
    );
  }, [
    group.deliveryDate,
    optimisticDeliveryDate,
    isStandbyGroup,
    hasLocationSet,
    isEditing,
    destinationLeadTimeZoneId,
  ]);

  useEffect(() => {
    const validDeliveryDate = !isNaN(latestStandardDeliveryDate.getTime());

    setCanShowDatePicker(
      hasLocationSet &&
        validDeliveryDate &&
        !isStandbyGroup &&
        !!destinationLeadTimeZoneId
    );

    setNeedsDeliveryDate(
      !isStandbyGroup &&
        destinationLeadTimeZoneId &&
        !optimisticDeliveryDate &&
        hasLocationSet
    );
  }, [
    hasLocationSet,
    latestStandardDeliveryDate,
    isStandbyGroup,
    destinationLeadTimeZoneId,
    optimisticDeliveryDate,
  ]);

  useEffect(() => {
    if (
      prevDeliveryDateRef.current &&
      !group.deliveryDate &&
      destinationFeeZoneId &&
      prevDestinationZoneRef.current !== destinationFeeZoneId
    ) {
      setOptimisticDeliveryDate(undefined);
      setIsEditing(true);
      setIsDatePickerExiting(false);

      hasCalculatedMinDate.current = false;
    }

    prevDeliveryDateRef.current = group.deliveryDate;
    prevDestinationZoneRef.current = destinationFeeZoneId;
  }, [group.deliveryDate, destinationFeeZoneId]);

  useEffect(() => {
    if (
      locationUpdateJustCompleted &&
      !group.deliveryDate &&
      !isStandbyGroup &&
      hasLocationSet
    ) {
      setIsEditing(true);
      setIsDatePickerExiting(false);

      if (destinationLeadTimeZoneId && destinationFeeZoneId) {
        const fetchLatestStandardDeliveryDate = async () => {
          setIsCalculatingDates(true);
          const startTime = performance.now();
          setCalculationStartTime(startTime);

          console.log(
            `⏱️ [CART-GROUP] Starting delivery calculation for group ${group.id}:`,
            {
              groupId: group.id,
              startTime: new Date().toISOString(),
              performanceStart: startTime,
              triggerContext: "fetchLatestStandardDeliveryDate useEffect",
            }
          );

          try {
            // Validate required data first - no fallbacks for critical partner data
            const firstItem = group.items[0];
            if (
              !firstItem?.product?.logistics ||
              !firstItem?.product?.partner?.daysOpen
            ) {
              console.warn(
                "CartGroup: Missing required logistics or partner schedule data",
                {
                  firstItemId: firstItem?.cartCustomId,
                  hasLogistics: !!firstItem?.product?.logistics,
                  hasPartnerSchedule: !!firstItem?.product?.partner?.daysOpen,
                }
              );
              throw new Error(
                "Missing required product data for delivery calculation"
              );
            }

            const originFeeZoneId =
              firstItem.product.partner?.deliveryFeeZone?.documentId;
            const originLeadTimeZoneId =
              firstItem.product.partner?.deliveryLeadTimeZone?.documentId;

            if (!originFeeZoneId || !originLeadTimeZoneId) {
              console.warn(
                "CartGroup: Missing required zone data for delivery calculation",
                {
                  originFeeZoneId,
                  originLeadTimeZoneId,
                  firstItemId: firstItem.cartCustomId,
                }
              );
              throw new Error("Missing zone data for delivery calculation");
            }

            // Validate limitTime to ensure it's a valid string
            const limitTime = firstItem.product.logistics.limitTime;
            if (!limitTime || typeof limitTime !== "string") {
              console.warn("CartGroup: Invalid or missing limitTime", {
                limitTime,
                firstItemId: firstItem.cartCustomId,
              });
              throw new Error("Invalid limitTime for delivery calculation");
            }

            // Create delivery calculation input with validated data (no fallbacks)
            const deliveryInput = {
              logistics: {
                expressDelivery:
                  firstItem.product.logistics.expressDelivery || false,
                limitTime: limitTime,
                daysToDeliver: firstItem.product.logistics.daysToDeliver || 1, // Only acceptable fallback
                minDaysToDeliver: firstItem.product.logistics.minDaysToDeliver,
              },
              partnerSchedule: firstItem.product.partner.daysOpen,
              zones: {
                originFeeZoneId,
                originLeadTimeZoneId,
                destinationFeeZoneId: destinationFeeZoneId,
                destinationLeadTimeZoneId: destinationLeadTimeZoneId,
              },
              opsLeadTime: 1, // This is the only acceptable default as it's operational, not partner-specific
            };

            const routeAvailability = {
              express: false, // Focus on standard delivery for cart groups
              rapido: false,
              standard: true,
            };

            const deliveryResult =
              await UnifiedDeliveryCalculationService.calculateDeliveryOptions(
                deliveryInput,
                routeAvailability
              );

            // Extract the standard delivery date
            const date = deliveryResult.standard.earliestDate;

            if (date && !isNaN(date.getTime())) {
              const limaDate = new Date(
                date.getFullYear(),
                date.getMonth(),
                date.getDate(),
                12,
                0,
                0
              );
              setMinDate(limaDate);
              setLatestStandardDeliveryDate(limaDate);
            }
          } catch (error) {
            console.error("Error calculating new delivery dates:", error);
          } finally {
            setIsCalculatingDates(false);
          }
        };

        fetchLatestStandardDeliveryDate();
      }
    }
  }, [
    locationUpdateJustCompleted,
    group.deliveryDate,
    isStandbyGroup,
    hasLocationSet,
    destinationLeadTimeZoneId,
    destinationFeeZoneId,
    group,
  ]);

  useEffect(() => {
    if (isEditing && calculatedMinDateRef.current) {
      setMinDate(calculatedMinDateRef.current);
      setLatestStandardDeliveryDate(calculatedMinDateRef.current);
    }
  }, [isEditing, group.id, minDate]);

  const handleDeliveryDateSelect = async (date: string) => {
    setOptimisticDeliveryDate(date);

    setIsEditing(false);
    setIsDatePickerExiting(true);

    const dateUpdateMsgId = `date-update-${group.id}`;
    const dateSuccessMsgId = `date-success-${group.id}`;
    const dateErrorMsgId = `date-error-${group.id}`;

    showLoadingMessage(
      "Actualizando fecha de entrega...",
      false,
      8000,
      dateUpdateMsgId
    );

    try {
      const success = await updateDeliveryDate(group.id, date);
      hideMessage(dateUpdateMsgId);

      if (success) {
        showSuccessMessage(
          "¡Fecha actualizada con éxito!",
          2000,
          dateSuccessMsgId
        );
        onDeliveryDateChange?.(group.id, date);
      } else {
        showErrorMessage(
          "Error al actualizar la fecha. Inténtalo nuevamente.",
          undefined,
          3000,
          dateErrorMsgId
        );
      }
    } catch (error) {
      hideMessage(dateUpdateMsgId);
      showErrorMessage(
        "Error al actualizar la fecha. Inténtalo nuevamente.",
        undefined,
        3000,
        dateErrorMsgId
      );
    }
  };

  const handleCancel = () => {
    if (group.deliveryDate) {
      setIsDatePickerExiting(true);
      setIsEditing(false);
    } else {
      const cancelWarningMsgId = `cancel-warning-${group.id}`;

      showWarningMessage(
        "Debes seleccionar una fecha de entrega para continuar",
        undefined,
        3000,
        cancelWarningMsgId
      );
    }
  };

  const handleDatePickerExited = () => {
    setIsDatePickerExiting(false);
  };

  useEffect(() => {
    if (isGroupUpdating) {
      prevOperationTypeRef.current = operationType;
    } else if (prevOperationTypeRef.current) {
      lastCompletedOperationRef.current = prevOperationTypeRef.current;
      prevOperationTypeRef.current = null;
    }
  }, [isGroupUpdating, operationType]);

  useEffect(() => {
    if (isGroupUpdating) {
      const messageContent =
        operationType === "DATE_UPDATE"
          ? "Actualizando fecha de entrega..."
          : operationType === "FEE_CALCULATION"
          ? "Calculando costo de envío..."
          : operationType === "QUANTITY_UPDATE" ||
            operationType === "ITEM_REMOVAL"
          ? "Actualizando productos..."
          : "Actualizando carrito...";

      const hasExistingMsg = messages.some(
        (msg) =>
          msg.content === messageContent && msg.type === MessageType.LOADING
      );

      if (hasExistingMsg) return;

      const operationMsgId = `operation-${operationType}-${group.id}`;
      showLoadingMessage(messageContent, false, 8000, operationMsgId);
    } else if (lastCompletedOperationRef.current) {
      const relatedLoadingMessages = messages.filter(
        (msg) =>
          msg.type === MessageType.LOADING &&
          ((lastCompletedOperationRef.current === "DATE_UPDATE" &&
            msg.content === "Actualizando fecha de entrega...") ||
            (lastCompletedOperationRef.current === "FEE_CALCULATION" &&
              msg.content === "Calculando costo de envío...") ||
            ((lastCompletedOperationRef.current === "QUANTITY_UPDATE" ||
              lastCompletedOperationRef.current === "ITEM_REMOVAL") &&
              msg.content === "Actualizando productos...") ||
            msg.content === "Actualizando carrito...")
      );

      relatedLoadingMessages.forEach((msg) => hideMessage(msg.id));

      if (relatedLoadingMessages.length > 0) {
        const hasDateSuccessMsg = messages.some(
          (msg) =>
            msg.content === "¡Fecha actualizada con éxito!" &&
            msg.type === MessageType.SUCCESS
        );

        if (!hasDateSuccessMsg) {
          const successContent =
            lastCompletedOperationRef.current === "QUANTITY_UPDATE"
              ? "¡Productos actualizados!"
              : lastCompletedOperationRef.current === "ITEM_REMOVAL"
              ? "¡Producto eliminado!"
              : lastCompletedOperationRef.current === "FEE_CALCULATION"
              ? "¡Costos actualizados!"
              : "¡Actualizado con éxito!";

          const operationSuccessMsgId = `success-${lastCompletedOperationRef.current}-${group.id}`;
          showSuccessMessage(successContent, 2000, operationSuccessMsgId);
        }
      }

      lastCompletedOperationRef.current = null;
    }
  }, [
    isGroupUpdating,
    operationType,
    messages,
    showLoadingMessage,
    showSuccessMessage,
    hideMessage,
    group.id,
  ]);

  useEffect(() => {
    if (stockError && outOfStockItems.length > 0) {
      const stockErrorMsgId = `stock-error-${group.id}`;

      showErrorMessage(
        "Algunos productos no tienen stock suficiente",
        <button
          className={styles.removeOutOfStockButton}
          onClick={() => {
            outOfStockItems.forEach((cartCustomId) => {
              const item = group.items.find(
                (p: CartProductType) => p.cartCustomId === cartCustomId
              );
              if (item) {
                handleRemove(item);
              }
            });

            setOutOfStockItems([]);
            setStockError(false);
          }}
        >
          Eliminar productos sin stock
        </button>,
        0,
        stockErrorMsgId
      );
    } else if (!stockError) {
      const hasStockError = messages.some(
        (msg) => msg.content === "Algunos productos no tienen stock suficiente"
      );

      if (hasStockError) {
        hideAllMessages();
      }
    }
  }, [
    stockError,
    outOfStockItems,
    group.items,
    group.id,
    showErrorMessage,
    hideAllMessages,
    handleRemove,
    messages,
  ]);

  useEffect(() => {
    if (needsDeliveryDate && !isEditing) {
      const dateWarningMsgId = `date-warning-${group.id}`;

      showWarningMessage(
        "Debes seleccionar una fecha de entrega",
        <button
          className={styles.selectDateButton}
          onClick={() => setIsEditing(true)}
        >
          Seleccionar fecha
        </button>,
        0,
        dateWarningMsgId
      );
    } else if (!needsDeliveryDate) {
      const hasMissingDateWarning = messages.some(
        (msg) => msg.content === "Debes seleccionar una fecha de entrega"
      );

      if (hasMissingDateWarning) {
        hideAllMessages();
      }
    }
  }, [
    needsDeliveryDate,
    isEditing,
    messages,
    group.id,
    showWarningMessage,
    hideAllMessages,
  ]);

  useEffect(() => {
    return () => {
      hideAllMessages();
    };
  }, [hideAllMessages]);

  const isDatePickerVisible = useMemo(() => {
    return isEditing && !isDatePickerExiting;
  }, [isEditing, isDatePickerExiting]);

  const visibleProducts = useMemo(() => {
    return group.items.filter((product) => product.quantity > 0);
  }, [group.items]);

  const optimisticSubtotal = useMemo(() => {
    const optimisticProducts = visibleProducts.map((product) => ({
      ...product,
      quantity: getOptimisticQuantity(product.cartCustomId, product.quantity),
    }));

    // Phase 2.3.3: Use clean pricing for subtotal calculation with fallback
    // NOTE: This is now used as fallback when groupTotals is not available
    if (optimisticProducts.some((p) => p.cleanPricing)) {
      return optimisticProducts.reduce((total, product) => {
        const quantity = product.quantity;
        if (product.cleanPricing) {
          // Use original price + customization for subtotal (before discounts)
          const unitPrice =
            product.cleanPricing.pricing.originalUnitPrice +
            product.cleanPricing.pricing.customizationSurcharge;
          return total + unitPrice * quantity;
        } else {
          // Fallback to legacy calculation
          return total + (product.finalPrice * quantity) / product.quantity;
        }
      }, 0);
    }

    // Fallback to legacy calculation
    return calculateItemsSubtotal(optimisticProducts || []);
  }, [visibleProducts, getOptimisticQuantity]);

  useEffect(() => {
    setIsFeeCalculating(
      isGroupUpdating &&
        (operationType === "FEE_CALCULATION" ||
          operationType === "QUANTITY_UPDATE" ||
          operationType === "ITEM_REMOVAL")
    );
  }, [isGroupUpdating, operationType]);

  const isDeliveryFeeCalculating = useMemo(() => {
    if (isStandbyGroup || !destinationFeeZoneId) return false;
    if (isFeeCalculating) return true;
    if (group.deliveryFee === undefined || group.deliveryFee === null)
      return true;
    if (group.deliveryFee === -1) return true;
    return false;
  }, [
    isStandbyGroup,
    destinationFeeZoneId,
    isFeeCalculating,
    group.deliveryFee,
  ]);

  const shouldShowPorCalcular = useMemo(() => {
    return isStandbyGroup || !destinationFeeZoneId;
  }, [isStandbyGroup, destinationFeeZoneId]);

  const isGroupTotalUpdating = useMemo(() => {
    return (
      isDeliveryFeeCalculating ||
      (isGroupUpdating && operationType === "QUANTITY_UPDATE")
    );
  }, [isDeliveryFeeCalculating, isGroupUpdating, operationType]);

  const formatDate = (date: string | undefined) => {
    if (!date) return "Fecha no seleccionada";
    if (date.includes("T") && date.includes("Z")) {
      return DateService.formatDeliveryDateForDisplay(date);
    } else if (date.includes("-")) {
      return DateService.formatDeliveryDateForDisplay(
        DateService.createDateFromApiFormat(date)
      );
    } else if (date.includes("/")) {
      const parts = date.split("/").map((part) => part.trim());
      if (parts.length === 3) {
        try {
          const day = parseInt(parts[0], 10);
          const month = parseInt(parts[1], 10) - 1;
          const year = parseInt(parts[2], 10);

          const dateObj = DateService.createLimaDate(year, month, day);
          return DateService.formatDeliveryDateForDisplay(dateObj);
        } catch (error) {
          return "Fecha inválida";
        }
      }
    }
    return DateService.formatDeliveryDateForDisplay(date);
  };

  const headerClasses = useMemo(() => {
    let classes = styles.header;
    if (isGroupUpdating) classes += ` ${styles.headerUpdating}`;
    if (needsDeliveryDate) classes += ` ${styles.headerNeedsDate}`;
    return classes;
  }, [isGroupUpdating, needsDeliveryDate]);

  const handleOptimisticCartAmountChange = (newQuantity: number, item: any) => {
    const currentQuantity = getOptimisticQuantity(
      item.cartCustomId,
      item.quantity
    );
    const delta = newQuantity - currentQuantity;

    if (Math.abs(delta) > 1) {
      const productId = item.cartCustomId || item.product?.id || "unknown";
      const quantityUpdateMsgId = `quantity-update-${productId}-${group.id}`;
      showLoadingMessage(
        "Actualizando productos...",
        false,
        8000,
        quantityUpdateMsgId
      );
    }

    if (delta > 0) {
      for (let i = 0; i < delta; i++) {
        handleIncrease(item);
      }
    } else if (delta < 0) {
      for (let i = 0; i < Math.abs(delta); i++) {
        handleDecrease(item);
      }
    }
  };

  const handleEditClick = () => {
    if (calculatedMinDateRef.current) {
      setMinDate(calculatedMinDateRef.current);
      setLatestStandardDeliveryDate(calculatedMinDateRef.current);
    }

    setIsEditing(true);
    setIsDatePickerExiting(false);
  };

  // Phase 2.2: Handler for delivery method changes - REMOVED
  // Now using per-product delivery method selection in CartItem

  // Handle available dates change from DeliveryMethodSelector
  const handleAvailableDatesChange = (dates: Date[], method: string) => {
    console.log(
      `📅 [CART-GROUP] Received available dates for method ${method}:`,
      {
        groupId: group.id,
        method,
        datesCount: dates.length,
        receivedDates: dates.map((d) => ({
          iso: d.toISOString(),
          local: d.toLocaleDateString("es-PE"),
          time: d.toLocaleTimeString("es-PE"),
        })),
        currentMinDate: {
          iso: minDate.toISOString(),
          local: minDate.toLocaleDateString("es-PE"),
        },
        currentLatestStandardDate: {
          iso: latestStandardDeliveryDate.toISOString(),
          local: latestStandardDeliveryDate.toLocaleDateString("es-PE"),
        },
        businessRule:
          method === "rapido"
            ? "Next 2 calendar days from today"
            : "Latest date among all products",
      }
    );

    setAvailableDeliveryDates(dates);
    setSelectedDeliveryMethod(method);

    // For rápido method, update the minDate for the date picker
    if (method === "rapido" && dates.length > 0) {
      const earliestDate = dates[0]; // First available rápido date
      const previousMinDate = minDate;

      console.log(`🔄 [CART-GROUP] Updating minDate for rápido delivery:`, {
        groupId: group.id,
        previousMinDate: {
          iso: previousMinDate.toISOString(),
          local: previousMinDate.toLocaleDateString("es-PE"),
        },
        newMinDate: {
          iso: earliestDate.toISOString(),
          local: earliestDate.toLocaleDateString("es-PE"),
        },
        datesDifferent: previousMinDate.getTime() !== earliestDate.getTime(),
        rapidoAvailableDates: dates.map((d) => d.toLocaleDateString("es-PE")),
        businessLogic:
          "Rápido: Use earliest available date from next 2 calendar days",
      });

      setMinDate(earliestDate);

      // If we don't have a delivery date yet, or if the current date is not in available dates
      if (!group.deliveryDate) {
        setNeedsDeliveryDate(true);
        setCanShowDatePicker(true);
      }
    }
  };

  const renderDeliveryFeeRow = () => (
    <div className={styles.row}>
      <span>Costo de envío:</span>
      <span className={styles.value}>
        {shouldShowPorCalcular ? (
          "Por calcular"
        ) : isDeliveryFeeCalculating || group.deliveryFee === -1 ? (
          <span className={styles.calculatingFee}>
            <span className={styles.miniSpinner}></span>
            Calculando
          </span>
        ) : group.deliveryFee === 0 ? (
          <span className={styles.freeDelivery}>Gratis</span>
        ) : (
          `S/ ${(group.deliveryFee || 0).toFixed(2)}`
        )}
      </span>
    </div>
  );

  const renderTotalRow = () => (
    <div className={`${styles.row} ${styles.total}`}>
      <span>Total:</span>
      <span className={styles.value}>
        {shouldShowPorCalcular ? (
          "Por calcular"
        ) : isGroupTotalUpdating ? (
          <span className={styles.calculatingTotal}>
            <span className={styles.miniSpinner}></span>
            Calculando
          </span>
        ) : (
          `S/ ${(
            optimisticSubtotal +
            (group.deliveryFee || 0) -
            (group.promotionalDiscountAmount || 0) -
            (group.couponDiscountAmount || 0)
          ).toFixed(2)}`
        )}
      </span>
    </div>
  );

  const renderDeliveryDateSection = () => {
    if (!hasLocationSet) {
      return (
        <div className={styles.noLocationWarning}>
          <span>
            Selecciona una ubicación para ver las fechas de entrega disponibles
          </span>
        </div>
      );
    }

    if (!canShowDatePicker) {
      return (
        <div className={styles.calculatingDatesMessage}>
          <span className={styles.spinningIcon}></span>
          <span>Calculando fechas de entrega disponibles...</span>
        </div>
      );
    }

    const shouldForceShowPlaceholder = !!(
      locationUpdateJustCompleted ||
      (prevDeliveryDateRef.current && !group.deliveryDate)
    );

    return (
      <div
        className={`${styles.datePickerContainer} ${
          isDatePickerExiting ? styles.exiting : ""
        }`}
        onTransitionEnd={() => {
          if (isDatePickerExiting) handleDatePickerExited();
        }}
      >
        <div className={styles.datePickerSection}>
          <p className={styles.datePickerLabel}>
            {!group.deliveryDate
              ? "Selecciona una fecha de entrega para continuar:"
              : "Selecciona la fecha de envío:"}
          </p>
          <div className={styles.datePickerControls}>
            <DeliveryDatePicker
              groupId={group.id}
              minDate={minDate}
              deliveryDate={
                shouldForceShowPlaceholder ? "" : optimisticDeliveryDate ?? ""
              }
              onDateChange={handleDeliveryDateSelect}
              forceShowPlaceholder={shouldForceShowPlaceholder}
              availableDates={
                selectedDeliveryMethod === "rapido"
                  ? availableDeliveryDates
                  : undefined
              }
              deliveryMethod={group.deliveryMethod || "standard"}
            />
            {group.deliveryDate && (
              <button onClick={handleCancel} className={styles.cancelButton}>
                Cancelar
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Phase 2.2: Group-level delivery method section removed
  // Now using per-product delivery method selection in CartItem

  // TEMPORARILY DISABLED - Delivery date validation effect to fix crashes
  // TODO: Re-enable after fixing potential infinite loop issues

  // Original validation logic is commented out below:
  /*
  const validateSavedDeliveryDate = async () => {
    // ... validation logic ...
  };
  
  useEffect(() => {
    validateSavedDeliveryDate();
  }, [group.id, group.deliveryDate]);
  */

  // Simple business rule validation for calculated dates
  const validateDateAgainstBusinessRules = (date: Date, method: string) => {
    const dayOfWeek = date.getDay(); // 0 = Sunday

    // Rule: No Sunday deliveries for pickup logistics
    if (dayOfWeek === 0) {
      return {
        isValid: false,
        reason: `${method} delivery scheduled for Sunday (not allowed)`,
      };
    }

    // Additional rules can be added here
    return { isValid: true };
  }; // SIMPLE TEST - COMPONENT RENDERING CHECK
  console.log(
    `🟢 [CART-GROUP] CartGroup component rendering for group ${group.id}:`,
    {
      groupId: group.id,
      deliveryMethod: group.deliveryMethod,
      hasLocationSet,
      destinationLeadTimeZoneId,
      destinationFeeZoneId,
      itemsCount: group.items.length,
    }
  );

  return (
    <div
      className={`${styles.cartGroup} ${
        needsDeliveryDate && hasLocationSet ? styles.needsDate : ""
      } ${!hasLocationSet ? styles.noLocation : ""}`}
      data-group-id={group.id}
    >
      <div className={headerClasses}>
        <div className={styles.headerContent}>
          <div className={styles.headerTextContainer}>
            <h2>
              {isStandbyGroup || !destinationLeadTimeZoneId || !hasLocationSet
                ? "Esperando información de envío"
                : isEditing
                ? isCalculatingDates || !canShowDatePicker
                  ? "Calculando fechas disponibles..."
                  : `Disponible desde el ${DateService.formatDeliveryDateForDisplay(
                      minDate
                    )}`
                : `Entrega el ${formatDate(optimisticDeliveryDate)}`}
            </h2>

            {/* Delivery Method Indicator - Enhanced for different states */}
            {!isStandbyGroup && hasLocationSet && (
              <>
                {/* When editing (no delivery date selected yet) */}
                {isEditing && !isCalculatingDates && canShowDatePicker && (
                  <span className={styles.deliveryMethodType}>
                    Envío{" "}
                    {group.deliveryMethod
                      ? getDeliveryMethodLabel(
                          group.deliveryMethod as any
                        ).toLowerCase()
                      : "estándar"}
                  </span>
                )}

                {/* When delivery date is already set */}
                {!isEditing && group.deliveryMethod && (
                  <p className={styles.deliveryMethodIndicator}>
                    {getDeliveryMethodLabel(group.deliveryMethod as any)}
                  </p>
                )}
              </>
            )}
          </div>
          {!isStandbyGroup &&
            destinationLeadTimeZoneId &&
            hasLocationSet &&
            !isEditing && (
              <button
                onClick={handleEditClick}
                className={styles.addDateButton}
                aria-label={
                  optimisticDeliveryDate ? "Editar fecha" : "Seleccionar Fecha"
                }
                disabled={isGroupUpdating || !canShowDatePicker}
              >
                {optimisticDeliveryDate ? "Editar fecha" : "Seleccionar Fecha"}
              </button>
            )}
        </div>
      </div>

      {isDatePickerVisible && canShowDatePicker && renderDeliveryDateSection()}

      <div className={styles.items}>
        {visibleProducts.map((product: any) => {
          // Check if this product is affected by the coupon
          const productId =
            product.product.id?.toString() || product.product.documentId;
          const isAffectedByCoupon =
            productId && affectedProductIds.includes(productId);

          // Calculate individual coupon discount for this product
          let couponDiscountAmount = 0;
          if (isAffectedByCoupon && couponDetails?.discount) {
            const optimisticQuantity = getOptimisticQuantity(
              product.cartCustomId,
              product.quantity
            );
            const productSubtotal =
              (product.finalPrice * optimisticQuantity) / product.quantity;

            if (couponDetails.discount.type === "percentage") {
              couponDiscountAmount =
                (productSubtotal * couponDetails.discount.value) / 100;
            } else if (couponDetails.discount.type === "fixed") {
              // For fixed discounts, distribute proportionally among affected products
              const affectedProducts = visibleProducts.filter((p: any) => {
                const pid = p.product.id?.toString() || p.product.documentId;
                return pid && affectedProductIds.includes(pid);
              });

              if (affectedProducts.length > 0) {
                const totalAffectedSubtotal = affectedProducts.reduce(
                  (sum, p) => {
                    const qty = getOptimisticQuantity(
                      p.cartCustomId,
                      p.quantity
                    );
                    return sum + (p.finalPrice * qty) / p.quantity;
                  },
                  0
                );

                if (totalAffectedSubtotal > 0) {
                  couponDiscountAmount =
                    (productSubtotal / totalAffectedSubtotal) *
                    couponDetails.discount.value;
                }
              }
            }
          }

          return (
            <CartItem
              key={product.cartCustomId ?? ""}
              item={{
                ...product,
                groupId: group.id,
                group: group, // Pass full group object for DeliveryMethodSelector
              }}
              handleCartAmountChange={handleOptimisticCartAmountChange}
              handleRemoveItem={handleRemove}
              optimisticQuantity={getOptimisticQuantity(
                product.cartCustomId,
                product.quantity
              )}
              stockValidation={stockValidation?.items.find(
                (item) => item.cartCustomId === product.cartCustomId
              )}
              allowRemoveOnOutOfStock={true}
              appliedCouponCode={appliedCouponCode}
              isAffectedByCoupon={isAffectedByCoupon}
              couponDiscountAmount={couponDiscountAmount}
              onAvailableDatesChange={handleAvailableDatesChange}
            />
          );
        })}

        {visibleProducts.length === 0 && (
          <div className={styles.emptyProducts}>
            <p>No hay productos en este grupo</p>
          </div>
        )}

        <div className={styles.groupMessagesContainer}>
          <GroupMessages
            messages={messages}
            isExiting={isMessagesExiting}
            onExited={handleMessagesExited}
          />
        </div>
      </div>

      <div className={styles.footer}>
        {(() => {
          console.log("DEBUG CartGroup Footer - Complete Group Data:", {
            "group keys": Object.keys(group),
            group: group,
            "group.groupTotals": group.groupTotals,
            "group.groupTotals keys": group.groupTotals
              ? Object.keys(group.groupTotals)
              : "no groupTotals",
            appliedCouponCode: appliedCouponCode,
            couponDetails: couponDetails,
            affectedProductIds: affectedProductIds,
          });

          // Check if any items have discount-related properties
          const itemsWithDiscounts = group.items?.map((item: any) => ({
            cartCustomId: item.cartCustomId,
            hasAnyDiscountProps:
              "promotionalDiscountAmount" in item ||
              "couponDiscountAmount" in item ||
              "pricing" in item,
            promotionalAmount:
              item.promotionalDiscountAmount ||
              item.pricing?.discounts?.promotional?.totalAmount ||
              0,
            couponAmount:
              item.couponDiscountAmount ||
              item.pricing?.discounts?.coupon?.totalAmount ||
              0,
            availableKeys: Object.keys(item),
          }));

          console.log(
            "DEBUG CartGroup Footer - Items with Discounts:",
            itemsWithDiscounts
          );

          return null;
        })()}
        <div className={styles.footerContent}>
          <div className={styles.row}>
            <span>Subtotal:</span>
            <span className={styles.value}>
              {/* Phase 2.3.3: Use backend-calculated groupTotals */}
              {isPricingLoading ? (
                <div className={styles.pricingLoader}>
                  <CircularProgress size={14} />
                  <span className={styles.loadingText}>Calculando...</span>
                </div>
              ) : (
                `S/ ${(
                  group.groupTotals?.originalSubtotal || optimisticSubtotal
                ).toFixed(2)}`
              )}
            </span>
          </div>

          {/* Promotional discount row */}
          {(() => {
            const promotionalDiscount =
              group.groupTotals?.promotionalDiscountTotal ||
              group.promotionalDiscountAmount ||
              0;
            console.log(
              "DEBUG CartGroup Footer - Promotional Discount Values:",
              {
                "group.groupTotals?.promotionalDiscountTotal":
                  group.groupTotals?.promotionalDiscountTotal,
                "group.promotionalDiscountAmount":
                  group.promotionalDiscountAmount,
                "final promotionalDiscount": promotionalDiscount,
                "condition result": promotionalDiscount > 0,
              }
            );
            return (
              promotionalDiscount > 0 && (
                <div className={`${styles.row} ${styles.promotionalRow}`}>
                  <span>Descuentos promocionales:</span>
                  <span
                    className={`${styles.value} ${styles.promotionalDiscount}`}
                  >
                    -S/ {promotionalDiscount.toFixed(2)}
                  </span>
                </div>
              )
            );
          })()}

          {/* Coupon discount row */}
          {(() => {
            const couponDiscount =
              group.groupTotals?.couponDiscountTotal ||
              group.couponDiscountAmount ||
              0;
            console.log("DEBUG CartGroup Footer - Coupon Discount Values:", {
              "group.groupTotals?.couponDiscountTotal":
                group.groupTotals?.couponDiscountTotal,
              "group.couponDiscountAmount": group.couponDiscountAmount,
              "final couponDiscount": couponDiscount,
              appliedCouponCode: appliedCouponCode,
              "condition result": couponDiscount > 0 && appliedCouponCode,
            });
            return (
              couponDiscount > 0 &&
              appliedCouponCode && (
                <div className={`${styles.row} ${styles.couponRow}`}>
                  <span>Cupón {appliedCouponCode}:</span>
                  <span className={`${styles.value} ${styles.couponDiscount}`}>
                    -S/ {couponDiscount.toFixed(2)}
                  </span>
                </div>
              )
            );
          })()}

          {renderDeliveryFeeRow()}
          {renderTotalRow()}

          {needsDeliveryDate && !isDatePickerVisible && hasLocationSet && (
            <div className={styles.dateRequiredNote}>
              <button
                onClick={() => setIsEditing(true)}
                className={styles.selectRequiredDateButton}
                disabled={!canShowDatePicker}
              >
                Seleccionar fecha de entrega para continuar
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CartGroup;
